import { ProgressBar } from 'react-aria-components';
import { useId } from 'react';

interface ProgressCircleProps {
  /** Diameter in pixels. Defaults to 24. */
  size?: number;
  /** Accessible label. */
  'aria-label': string;
  /**
   * 0–100 for determinate mode (shows arc + value text).
   * null = no data (shows "—" with empty track).
   * Omit entirely for indeterminate (spinning loader).
   */
  value?: number | null;
  /** Arc stroke color — visually the start of the gradient (at 12 o'clock). */
  color?: string;
  /** When set, the arc renders a gradient: `color` at the arc start, `colorEnd` at the arc end. */
  colorEnd?: string;
}

/**
 * Circular progress indicator built on react-aria ProgressBar.
 * - No `value` prop → indeterminate spinning loader, always uses --bui-fg-info.
 * - `value={number}` → determinate arc showing the percentage + numeric label.
 * - `value={null}` → determinate track with no arc, shows "—".
 * - `colorEnd` → gradient arc from `color` to `colorEnd`.
 */
export const ProgressCircle = ({
  size = 24,
  'aria-label': ariaLabel,
  value,
  color = 'currentColor',
  colorEnd,
}: ProgressCircleProps) => {
  const gradId = useId();
  const r = size * 0.375;
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;
  const strokeWidth = size * 0.075;
  const isDeterminate = value !== undefined;
  const hasGradient = isDeterminate && colorEnd !== undefined;

  const resolveStroke = (spinning: boolean) => {
    if (spinning) return 'var(--bui-fg-announcement)';
    if (hasGradient) return `url(#${gradId})`;
    return color;
  };

  return (
    <ProgressBar
      isIndeterminate={!isDeterminate}
      value={typeof value === 'number' ? value : 0}
      minValue={0}
      maxValue={100}
      aria-label={ariaLabel}
    >
      {({ percentage, isIndeterminate: spinning }) => (
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          fill="none"
          aria-hidden="true"
        >
          {hasGradient && (
            <defs>
              <linearGradient
                id={gradId}
                gradientUnits="userSpaceOnUse"
                x1="0"
                y1="0"
                x2={size}
                y2={size}
              >
                <stop offset="0%" stopColor={colorEnd} />
                <stop offset="100%" stopColor={color} />
              </linearGradient>
            </defs>
          )}
          {/* Background track */}
          <circle
            cx={cx}
            cy={cx}
            r={r}
            stroke="var(--bui-border-1)"
            strokeWidth={strokeWidth}
          />
          {/* Arc */}
          <circle
            cx={cx}
            cy={cx}
            r={r}
            stroke={resolveStroke(spinning)}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={
              isDeterminate
                ? circumference * (1 - (percentage ?? 0) / 100)
                : circumference * 0.75
            }
            strokeLinecap="round"
            transform={isDeterminate ? `rotate(-90 ${cx} ${cx})` : undefined}
          >
            {spinning && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                dur="0.75s"
                values={`0 ${cx} ${cx};360 ${cx} ${cx}`}
                repeatCount="indefinite"
              />
            )}
          </circle>
          {/* Value label for determinate mode */}
          {isDeterminate && (
            <text
              x={cx}
              y={cx}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={size * 0.23}
              fontWeight="bold"
              fill="var(--bui-fg-primary)"
            >
              {value ?? '—'}
            </text>
          )}
        </svg>
      )}
    </ProgressBar>
  );
};
